import React, { useState, useEffect } from 'react';
import { useGetLandlordPropertyQuery } from '../../../lib/apiSlice/property/propertySlice.tsx';
import { useAddSMSCreditsMutation } from '../../../lib/apiSlice/communication/communicationSlice.tsx';

export default function AddCreditsModal({ onClose }) {
    const { data: propertiesData } = useGetLandlordPropertyQuery();
    const [addSMSCredits] = useAddSMSCreditsMutation();

    const properties = propertiesData?.results || [];

    const [activeTab, setActiveTab] = useState('single');
    const [selectedProperty, setSelectedProperty] = useState("");
    const [distributionMode, setDistributionMode] = useState("single");
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [amount, setAmount] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [unitSearchTerm, setUnitSearchTerm] = useState("");




    // Reset form when tab changes
    useEffect(() => {
        setSelectedProperty("");
        setSelectedUnits([]);
        setSelectedUnit(null);
        setDistributionMode("single");
    }, [activeTab]);

    // Reset unit selection when property changes
    useEffect(() => {
        setSelectedUnits([]);
        setSelectedUnit(null);
        setDistributionMode("single");
        setUnitSearchTerm("");
    }, [selectedProperty]);

    const handleUnitToggle = (unitId) => {
        setSelectedUnits(prev =>
            prev.includes(unitId)
                ? prev.filter(id => id !== unitId)
                : [...prev, unitId]
        );
    };


   
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const creditAmount = parseFloat(amount);
            // Validate phone number
            if (phoneNumber.length !== 10 || !phoneNumber.startsWith('0')) {
                throw new Error("Please enter a valid 10-digit phone number starting with 0");
            }
            const apiPhoneNumber = phoneNumber.substring(1);

            const smsUnits = Math.floor(creditAmount / 0.6);

            const data = {
                amount: creditAmount,
                sms_unit: smsUnits,
                phone_number: apiPhoneNumber,
                apartment_id: parseInt(selectedProperty)
            };

            // Call the RTK mutation
            const result = await addSMSCredits(data).unwrap();

            console.log('Success:', result);
            onClose();
        } catch (error) {
            console.error('Error adding SMS credits:', error);
            alert(`Error: ${error.message || 'Failed to add SMS credits'}`);
        }
    };

    // Get the currently selected property
    const currentProperty = properties.find(p => p.id === parseInt(selectedProperty));

    // Filter units based on search term
    const filteredUnits = currentProperty?.units?.filter(unit =>
        unit.name.toLowerCase().includes(unitSearchTerm.toLowerCase())
    ) || [];

    // if (isLoading) return <div className="p-4 text-center">Loading properties...</div>;
    // if (isError) return <div className="p-4 text-red-500 text-center">Error loading properties</div>;

    return (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="relative bg-white shadow-xl mx-4 rounded-lg w-full max-w-md overflow-hidden">
                <button
                    onClick={onClose}
                    className="top-4 right-4 absolute text-gray-500 hover:text-gray-700 transition-colors"
                    aria-label="Close modal"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>

                <div className="p-6">
                    <h2 className="mb-6 font-semibold text-gray-800 text-2xl">Add SMS Credits</h2>

                    <div className="mb-6 border-gray-200 border-b">
                        <nav className="flex space-x-8 -mb-px">
                            <button
                                type="button"
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'single' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                                onClick={() => setActiveTab('single')}
                            >
                                Single Property
                            </button>
                            <button
                                type="button"
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                                onClick={() => setActiveTab('all')}
                            >
                                All Properties
                            </button>
                        </nav>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {activeTab === 'single' && (
                            <div className="space-y-5">
                                <div>
                                    <p className="block mb-1 font-medium text-gray-700 text-sm">Property</p>
                                    <select
                                        className="shadow-sm px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full"
                                        value={selectedProperty}
                                        onChange={(e) => setSelectedProperty(e.target.value)}
                                        required
                                    >
                                        <option value="">Select a property</option>
                                        {properties.map(property => (
                                            <option key={property.id} value={property.id}>
                                                {property.name} ({property.location})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedProperty && (
                                    <div className='hidden'>
                                        <p className="block mb-2 font-medium text-gray-700 text-sm">Unit Distribution</p>
                                        <div className="gap-2 grid grid-cols-3 mb-3">
                                            {['single', 'multiple', 'all'].map(mode => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    className={`py-2 px-3 text-sm font-medium rounded-md ${distributionMode === mode ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-transparent'}`}
                                                    onClick={() => setDistributionMode(mode)}
                                                >
                                                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                                                </button>
                                            ))}
                                        </div>

                                        {['multiple', 'single'].includes(distributionMode) && (
                                            <>
                                                <input
                                                    type="text"
                                                    placeholder="Search unit..."
                                                    className="shadow-sm mb-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-300 w-full"
                                                    value={unitSearchTerm}
                                                    onChange={(e) => setUnitSearchTerm(e.target.value)}
                                                />

                                                <div className="p-3 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                                                    <div className="space-y-2">
                                                        {filteredUnits.length ? (
                                                            filteredUnits.map(unit => {
                                                                const isSelected = distributionMode === 'multiple'
                                                                    ? selectedUnits.includes(unit.id)
                                                                    : selectedUnit === unit.id;

                                                                return (
                                                                    <div
                                                                        key={unit.id}
                                                                        className={`flex items-center cursor-pointer p-2 rounded-md ${isSelected ? 'bg-blue-100 border border-blue-500' : 'hover:bg-gray-50'}`}
                                                                        onClick={() => {
                                                                            if (distributionMode === 'multiple') {
                                                                                handleUnitToggle(unit.id);
                                                                            } else {
                                                                                setSelectedUnit(unit.id);
                                                                            }
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type={distributionMode === 'multiple' ? "checkbox" : "radio"}
                                                                            name="unit"
                                                                            className="rounded focus:ring-blue-500 w-4 h-4 text-blue-600"
                                                                            checked={isSelected}
                                                                            onChange={() => {
                                                                                if (distributionMode === 'multiple') {
                                                                                    handleUnitToggle(unit.id);
                                                                                } else {
                                                                                    setSelectedUnit(unit.id);
                                                                                }
                                                                            }}
                                                                        />
                                                                        <p className="mt-3 ml-2 text-gray-700 text-sm">
                                                                            {unit.name} ({unit.occupancy_status})
                                                                        </p>
                                                                    </div>
                                                                );
                                                            })
                                                        ) : (
                                                            <p className="text-gray-500 text-sm">
                                                                {currentProperty?.units?.length
                                                                    ? "No units match your search."
                                                                    : "This property has no units."}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="space-y-5 mt-6">
                            <div>
                                <p className="block mb-1 font-medium text-gray-700 text-sm">Credit Amount (KES)</p>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    className="shadow-sm px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full"
                                    placeholder="Enter amount (minimum 0.6 KES)"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    required
                                />
                                {amount && (
                                    <p className="mt-1 text-gray-500 text-sm">
                                        This will buy {Math.floor(parseFloat(amount) / 0.6).toLocaleString()} SMS units
                                    </p>
                                )}
                            </div>

                            <div>
                                <p className="block mb-1 font-medium text-gray-700 text-sm">Phone Number</p>
                                <input
                                    type="tel"
                                    className="shadow-sm px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-blue-500 w-full"
                                    placeholder="Enter phone number"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    required
                                    pattern="0[0-9]{9}"
                                    maxLength={10}

                                />
                                <p className="mt-1 text-gray-500 text-sm">
                                    {phoneNumber.length}/10
                                </p>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="flex justify-center bg-blue-600 hover:bg-blue-700 shadow-sm mt-8 px-4 py-3 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full font-medium text-white text-sm"
                        >
                            Add Credits
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}